import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  serverTimestamp,
  orderBy,
  query
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaUpload, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import imageCompression from 'browser-image-compression';

const HeroManagement = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlide, setEditingSlide] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const emptySlide = {
    image: '',
    badge: '',
    title: '',
    subtitle: '',
    description: '',
    overlayType: 'gold',
    button1Text: 'Explore Collection',
    button1Link: '/categories',
    button1Variant: 'gold',
    button2Text: 'Learn More',
    button2Link: '/about',
    button2Variant: 'outline',
    order: 0,
    active: true
  };

  const [formData, setFormData] = useState(emptySlide);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const slidesCollection = collection(db, 'heroSlides');
      const q = query(slidesCollection, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const slidesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSlides(slidesList);
    } catch (error) {
      console.error('Error fetching slides:', error);
      setMessage({ type: 'error', text: 'Failed to load hero slides' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Compress image
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };
      
      const compressedFile = await imageCompression(file, options);
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, `hero-images/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, compressedFile);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setMessage({ type: 'error', text: 'Failed to upload image' });
          setIsUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setFormData(prev => ({ ...prev, image: downloadURL }));
          setIsUploading(false);
          setUploadProgress(0);
          setMessage({ type: 'success', text: 'Image uploaded successfully' });
        }
      );
    } catch (error) {
      console.error('Error compressing image:', error);
      setMessage({ type: 'error', text: 'Failed to process image' });
      setIsUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      if (!formData.image || !formData.title) {
        setMessage({ type: 'error', text: 'Image and title are required' });
        return;
      }

      if (editingSlide) {
        // Update existing slide
        const slideRef = doc(db, 'heroSlides', editingSlide.id);
        await updateDoc(slideRef, {
          ...formData,
          updatedAt: serverTimestamp()
        });
        setMessage({ type: 'success', text: 'Slide updated successfully' });
      } else {
        // Add new slide
        await addDoc(collection(db, 'heroSlides'), {
          ...formData,
          order: slides.length,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setMessage({ type: 'success', text: 'Slide added successfully' });
      }

      setEditingSlide(null);
      setIsAddingNew(false);
      setFormData(emptySlide);
      fetchSlides();
    } catch (error) {
      console.error('Error saving slide:', error);
      setMessage({ type: 'error', text: 'Failed to save slide' });
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      image: slide.image || '',
      badge: slide.badge || '',
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      description: slide.description || '',
      overlayType: slide.overlayType || 'gold',
      button1Text: slide.button1Text || 'Explore Collection',
      button1Link: slide.button1Link || '/categories',
      button1Variant: slide.button1Variant || 'gold',
      button2Text: slide.button2Text || 'Learn More',
      button2Link: slide.button2Link || '/about',
      button2Variant: slide.button2Variant || 'outline',
      order: slide.order || 0,
      active: slide.active !== undefined ? slide.active : true
    });
    setIsAddingNew(false);
  };

  const handleDelete = async (slideId) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;

    try {
      await deleteDoc(doc(db, 'heroSlides', slideId));
      setMessage({ type: 'success', text: 'Slide deleted successfully' });
      fetchSlides();
    } catch (error) {
      console.error('Error deleting slide:', error);
      setMessage({ type: 'error', text: 'Failed to delete slide' });
    }
  };

  const handleCancel = () => {
    setEditingSlide(null);
    setIsAddingNew(false);
    setFormData(emptySlide);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingSlide(null);
    setFormData({ ...emptySlide, order: slides.length });
  };

  const moveSlide = async (slideId, direction) => {
    const slideIndex = slides.findIndex(s => s.id === slideId);
    if (
      (direction === 'up' && slideIndex === 0) ||
      (direction === 'down' && slideIndex === slides.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? slideIndex - 1 : slideIndex + 1;
    const slide1 = slides[slideIndex];
    const slide2 = slides[newIndex];

    try {
      await updateDoc(doc(db, 'heroSlides', slide1.id), { order: newIndex });
      await updateDoc(doc(db, 'heroSlides', slide2.id), { order: slideIndex });
      fetchSlides();
    } catch (error) {
      console.error('Error reordering slides:', error);
      setMessage({ type: 'error', text: 'Failed to reorder slides' });
    }
  };

  if (loading) {
    return <Container><LoadingMessage>Loading hero slides...</LoadingMessage></Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Hero Slideshow Management</Title>
        {!isAddingNew && !editingSlide && (
          <AddButton onClick={handleAddNew}>
            <FaPlus /> Add New Slide
          </AddButton>
        )}
      </Header>

      {message.text && (
        <Message $type={message.type}>
          {message.text}
          <CloseButton onClick={() => setMessage({ type: '', text: '' })}>×</CloseButton>
        </Message>
      )}

      {(isAddingNew || editingSlide) && (
        <FormCard>
          <FormTitle>{editingSlide ? 'Edit Slide' : 'Add New Slide'}</FormTitle>
          
          <FormGroup>
            <Label>Hero Image*</Label>
            <ImageUploadContainer>
              {formData.image && (
                <PreviewImage src={formData.image} alt="Preview" />
              )}
              <UploadButton as="label">
                <FaUpload /> {formData.image ? 'Change Image' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={isUploading}
                />
              </UploadButton>
              {isUploading && <ProgressBar progress={uploadProgress} />}
            </ImageUploadContainer>
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Badge Text</Label>
              <Input
                type="text"
                name="badge"
                value={formData.badge}
                onChange={handleChange}
                placeholder="e.g., Premium Collection"
              />
            </FormGroup>

            <FormGroup>
              <Label>Overlay Type</Label>
              <Select name="overlayType" value={formData.overlayType} onChange={handleChange}>
                <option value="gold">Gold</option>
                <option value="silver">Silver</option>
                <option value="blue">Blue</option>
                <option value="dark">Dark</option>
              </Select>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Title*</Label>
            <Input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Main heading"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Subtitle</Label>
            <Input
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              placeholder="Secondary heading"
            />
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <TextArea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Slide description"
              rows="3"
            />
          </FormGroup>

          <SectionTitle>Button 1</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>Button 1 Text</Label>
              <Input
                type="text"
                name="button1Text"
                value={formData.button1Text}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Button 1 Link</Label>
              <Input
                type="text"
                name="button1Link"
                value={formData.button1Link}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Button 1 Style</Label>
              <Select name="button1Variant" value={formData.button1Variant} onChange={handleChange}>
                <option value="gold">Gold</option>
                <option value="primary">Primary</option>
                <option value="outline">Outline</option>
              </Select>
            </FormGroup>
          </FormRow>

          <SectionTitle>Button 2</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>Button 2 Text</Label>
              <Input
                type="text"
                name="button2Text"
                value={formData.button2Text}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Button 2 Link</Label>
              <Input
                type="text"
                name="button2Link"
                value={formData.button2Link}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Button 2 Style</Label>
              <Select name="button2Variant" value={formData.button2Variant} onChange={handleChange}>
                <option value="outline">Outline</option>
                <option value="gold">Gold</option>
                <option value="primary">Primary</option>
              </Select>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              Active (show in slideshow)
            </CheckboxLabel>
          </FormGroup>

          <ButtonGroup>
            <SaveButton onClick={handleSave}>
              <FaSave /> Save Slide
            </SaveButton>
            <CancelButton onClick={handleCancel}>
              <FaTimes /> Cancel
            </CancelButton>
          </ButtonGroup>
        </FormCard>
      )}

      <SlidesGrid>
        {slides.map((slide, index) => (
          <SlideCard key={slide.id} $active={slide.active}>
            <SlideImage src={slide.image} alt={slide.title} />
            <SlideContent>
              <SlideTitle>{slide.title}</SlideTitle>
              <SlideSubtitle>{slide.subtitle}</SlideSubtitle>
              {slide.badge && <Badge>{slide.badge}</Badge>}
              <SlideStatus $active={slide.active}>
                {slide.active ? 'Active' : 'Inactive'}
              </SlideStatus>
            </SlideContent>
            <SlideActions>
              <OrderButtons>
                <IconButton 
                  onClick={() => moveSlide(slide.id, 'up')}
                  disabled={index === 0}
                  title="Move up"
                >
                  <FaArrowUp />
                </IconButton>
                <IconButton 
                  onClick={() => moveSlide(slide.id, 'down')}
                  disabled={index === slides.length - 1}
                  title="Move down"
                >
                  <FaArrowDown />
                </IconButton>
              </OrderButtons>
              <IconButton onClick={() => handleEdit(slide)} title="Edit">
                <FaEdit />
              </IconButton>
              <IconButton onClick={() => handleDelete(slide.id)} $danger title="Delete">
                <FaTrash />
              </IconButton>
            </SlideActions>
          </SlideCard>
        ))}
      </SlidesGrid>

      {slides.length === 0 && !isAddingNew && (
        <EmptyMessage>
          No hero slides yet. Click "Add New Slide" to create your first slide.
        </EmptyMessage>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #C41E3A;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #a01829;
  }
`;

const Message = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  background-color: ${props => props.$type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$type === 'success' ? '#155724' : '#721c24'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: inherit;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2`
  margin: 0 0 1.5rem 0;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #555;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #C41E3A;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #C41E3A;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #C41E3A;
  }
`;

const ImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 4px;
`;

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #f0f0f0;
  border: 2px dashed #ccc;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #e0e0e0;
    border-color: #999;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.progress}%;
    background-color: #C41E3A;
    transition: width 0.3s;
  }
`;

const SectionTitle = styled.h3`
  margin: 1.5rem 0 1rem 0;
  color: #666;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  input {
    width: auto;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #218838;
  }
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #5a6268;
  }
`;

const SlidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const SlideCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  opacity: ${props => props.$active ? 1 : 0.6};
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-4px);
  }
`;

const SlideImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const SlideContent = styled.div`
  padding: 1rem;
`;

const SlideTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #333;
`;

const SlideSubtitle = styled.p`
  margin: 0 0 0.5rem 0;
  color: #666;
  font-size: 0.9rem;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: #D4AF37;
  color: white;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const SlideStatus = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${props => props.$active ? '#28a745' : '#6c757d'};
  color: white;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const SlideActions = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  border-top: 1px solid #eee;
`;

const OrderButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  padding: 0.5rem;
  background-color: ${props => props.$danger ? '#dc3545' : '#f0f0f0'};
  color: ${props => props.$danger ? 'white' : '#333'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: ${props => props.$danger ? '#c82333' : '#e0e0e0'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #999;
  font-size: 1.1rem;
`;

export default HeroManagement;
